// =============================================================================
// SimSense Marketplace — Search Autocomplete (OT-301)
// =============================================================================

import { useState, useRef, useEffect, useMemo } from "react";
import type { SetupProductPreview } from "../../../types/marketplace";
import "./SearchAutocomplete.css";

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  setups: SetupProductPreview[];
  placeholder?: string;
}

interface Suggestion {
  label: string;
  type: "car" | "track" | "creator" | "setup";
  value: string;
}

const TYPE_LABELS: Record<string, string> = {
  car: "Car",
  track: "Track",
  creator: "Creator",
  setup: "Setup",
};

export function SearchAutocomplete({ value, onChange, setups, placeholder }: SearchAutocompleteProps) {
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build unique suggestion pools from setup data
  const suggestions = useMemo((): Suggestion[] => {
    if (!value || value.length < 2) return [];
    const q = value.toLowerCase();
    const seen = new Set<string>();
    const results: Suggestion[] = [];

    for (const s of setups) {
      // Car names
      const carKey = `car:${s.carName}`;
      if (!seen.has(carKey) && s.carName.toLowerCase().includes(q)) {
        seen.add(carKey);
        results.push({ label: s.carName, type: "car", value: s.carName });
      }
      // Track names
      const trackKey = `track:${s.trackName}`;
      if (!seen.has(trackKey) && s.trackName.toLowerCase().includes(q)) {
        seen.add(trackKey);
        results.push({ label: s.trackName, type: "track", value: s.trackName });
      }
      // Creator names
      const creatorKey = `creator:${s.creatorName}`;
      if (!seen.has(creatorKey) && s.creatorName.toLowerCase().includes(q)) {
        seen.add(creatorKey);
        results.push({ label: s.creatorName, type: "creator", value: s.creatorName });
      }
      // Setup names
      const setupKey = `setup:${s.setupName}`;
      if (!seen.has(setupKey) && s.setupName.toLowerCase().includes(q)) {
        seen.add(setupKey);
        results.push({ label: s.setupName, type: "setup", value: s.setupName });
      }
    }

    return results.slice(0, 8);
  }, [value, setups]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset active index when suggestions change
  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions.length]);

  const showDropdown = focused && suggestions.length > 0;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      onChange(suggestions[activeIndex].value);
      setFocused(false);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  const selectSuggestion = (s: Suggestion) => {
    onChange(s.value);
    setFocused(false);
    inputRef.current?.focus();
  };

  return (
    <div className="search-autocomplete" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        className="search-input"
        placeholder={placeholder || "Search by car, track, creator, or setup name..."}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && (
        <div className="search-dropdown">
          {suggestions.map((s, i) => (
            <button
              key={`${s.type}:${s.label}`}
              className={`search-suggestion ${i === activeIndex ? "active" : ""}`}
              onMouseDown={() => selectSuggestion(s)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="suggestion-label">{highlightMatch(s.label, value)}</span>
              <span className={`suggestion-type type-${s.type}`}>{TYPE_LABELS[s.type]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function highlightMatch(text: string, query: string) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}
